import { useMemo, useState, type ReactElement } from 'react';
import type { TimelineSpan } from '../../api/admin.js';
import { aggregateByDimension, type StatDimension } from '../../lib/session-stats.js';
import { nanoToUsdCompact } from '../../lib/format.js';

interface CostDonutProps {
  spans: ReadonlyArray<TimelineSpan>;
  size?: number;
}

const KIND_COLORS: Record<string, string> = {
  llm: 'var(--k-llm)',
  tts: 'var(--k-tts)',
  stt: 'var(--k-stt)',
  pronunciation: 'var(--k-pronunciation)',
  web_search: 'var(--k-web_search)',
  voice: 'var(--k-voice)',
  tool_call: 'var(--k-tool_call)',
  retrieval: 'var(--k-retrieval)',
  guardrail: 'var(--k-guardrail)',
  agent_decision: 'var(--k-agent_decision)',
  http: 'var(--k-http)',
  db_query: 'var(--k-db_query)',
  pipeline_stage: 'var(--k-pipeline_stage)',
  module_step: 'var(--k-module_step)',
  job: 'var(--k-job)',
};

const PROVIDER_COLORS = [
  'oklch(65% 0.20 290)', // morado
  'oklch(70% 0.18 50)', // ámbar
  'oklch(70% 0.15 195)', // turquesa
  'oklch(72% 0.14 340)', // rosa
  'oklch(60% 0.16 140)', // verde
  'oklch(65% 0.14 220)', // azul
];

function colorFor(dim: StatDimension, key: string, index: number): string {
  if (dim === 'kind') return KIND_COLORS[key] ?? 'var(--text-tertiary)';
  return PROVIDER_COLORS[index % PROVIDER_COLORS.length]!;
}

/**
 * Donut chart de distribución de costos por kind o provider.
 * Click en una rebanada o leyenda highlightea esa categoría.
 */
export function CostDonut({ spans, size = 200 }: CostDonutProps): ReactElement {
  const [dim, setDim] = useState<StatDimension>('kind');
  const [hoverKey, setHoverKey] = useState<string | null>(null);
  const buckets = useMemo(() => aggregateByDimension(spans, dim), [spans, dim]);

  const total = useMemo(() => {
    let t = 0n;
    for (const b of buckets) t += b.costNano;
    return t;
  }, [buckets]);

  const totalNumber = useMemo(() => {
    if (total === 0n) return 0;
    // bigint → number; pierde precisión más allá de Number.MAX_SAFE_INTEGER
    // pero para arcs/charts es irrelevante (sólo necesitamos proporciones).
    if (total < BigInt(Number.MAX_SAFE_INTEGER)) return Number(total);
    return Number(total / 1_000_000n) * 1_000_000;
  }, [total]);

  const radius = size / 2;
  const stroke = size * 0.18;
  const innerRadius = radius - stroke;
  const cx = radius;
  const cy = radius;

  if (totalNumber === 0 || buckets.length === 0) {
    return (
      <div className="sess-chart-card" data-testid="cost-donut-empty">
        <div className="sess-chart-head">
          <span className="sess-chart-title">Costo</span>
          <DimToggle dim={dim} onChange={setDim} />
        </div>
        <div className="empty" style={{ height: size }}>
          Sin costos imputados en esta sesión.
        </div>
      </div>
    );
  }

  let accAngle = -Math.PI / 2;
  const slices = buckets
    .filter((b) => b.costNano > 0n)
    .map((b, i) => {
      // Mismo collapse a number; precisión irrelevante para arcs.
      const value =
        b.costNano < BigInt(Number.MAX_SAFE_INTEGER)
          ? Number(b.costNano)
          : Number(b.costNano / 1_000_000n) * 1_000_000;
      const fraction = value / totalNumber;
      const sweep = fraction * Math.PI * 2;
      const startAngle = accAngle;
      const endAngle = accAngle + sweep;
      accAngle = endAngle;
      const x1 = cx + radius * Math.cos(startAngle);
      const y1 = cy + radius * Math.sin(startAngle);
      const x2 = cx + radius * Math.cos(endAngle);
      const y2 = cy + radius * Math.sin(endAngle);
      const xi2 = cx + innerRadius * Math.cos(endAngle);
      const yi2 = cy + innerRadius * Math.sin(endAngle);
      const xi1 = cx + innerRadius * Math.cos(startAngle);
      const yi1 = cy + innerRadius * Math.sin(startAngle);
      const largeArc = sweep > Math.PI ? 1 : 0;
      // path donut: outer arc, line inward, inner arc reversed, close.
      const d = [
        `M ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`,
        `L ${xi2} ${yi2}`,
        `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${xi1} ${yi1}`,
        'Z',
      ].join(' ');
      return {
        key: b.key,
        value,
        fraction,
        cost: b.costNano,
        color: colorFor(dim, b.key, i),
        d,
      };
    });

  const focusedKey = hoverKey;

  return (
    <div className="sess-chart-card" data-testid="cost-donut">
      <div className="sess-chart-head">
        <span className="sess-chart-title">Costo</span>
        <DimToggle dim={dim} onChange={setDim} />
      </div>
      <div className="sess-donut-body">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {slices.map((s) => {
            const dimmed = focusedKey !== null && focusedKey !== s.key;
            return (
              <path
                key={s.key}
                d={s.d}
                fill={s.color}
                fillOpacity={dimmed ? 0.25 : 0.95}
                stroke="var(--surface-0)"
                strokeWidth={1}
                onMouseEnter={() => setHoverKey(s.key)}
                onMouseLeave={() => setHoverKey(null)}
                data-testid={`donut-slice-${s.key}`}
              >
                <title>{`${s.key}: ${nanoToUsdCompact(s.cost.toString())} (${(s.fraction * 100).toFixed(1)}%)`}</title>
              </path>
            );
          })}
          <text
            x={cx}
            y={cy - 6}
            textAnchor="middle"
            fontSize={11}
            fill="var(--text-tertiary)"
            fontFamily="var(--font-mono)"
          >
            total
          </text>
          <text
            x={cx}
            y={cy + 12}
            textAnchor="middle"
            fontSize={14}
            fill="var(--text-primary)"
            fontFamily="var(--font-mono)"
            fontWeight={500}
          >
            {nanoToUsdCompact(total.toString())}
          </text>
        </svg>
        <ul className="sess-chart-legend">
          {slices.map((s) => {
            const dimmed = focusedKey !== null && focusedKey !== s.key;
            return (
              <li
                key={s.key}
                className={`sess-legend-row${dimmed ? ' is-dim' : ''}`}
                onMouseEnter={() => setHoverKey(s.key)}
                onMouseLeave={() => setHoverKey(null)}
                data-testid={`donut-legend-${s.key}`}
              >
                <span className="sess-legend-swatch" style={{ background: s.color }} />
                <span className="sess-legend-key mono">{s.key}</span>
                <span className="sess-legend-val mono">
                  {nanoToUsdCompact(s.cost.toString())}
                </span>
                <span className="sess-legend-pct mono">
                  {(s.fraction * 100).toFixed(1)}%
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

interface DimToggleProps {
  dim: StatDimension;
  onChange: (next: StatDimension) => void;
}

function DimToggle({ dim, onChange }: DimToggleProps): ReactElement {
  return (
    <div className="seg-wf" role="group" aria-label="Dimensión">
      <button
        type="button"
        className={dim === 'kind' ? 'is-on' : ''}
        onClick={() => onChange('kind')}
        data-testid="donut-dim-kind"
      >
        kind
      </button>
      <button
        type="button"
        className={dim === 'provider' ? 'is-on' : ''}
        onClick={() => onChange('provider')}
        data-testid="donut-dim-provider"
      >
        provider
      </button>
    </div>
  );
}
